import React from 'react';
import {
  Info,
  Users,
  ShoppingCart,
  Heart,
  Smartphone,
  Save,
  Clock,
  CheckCircle,
  Star,
  Zap,
  Shield,
  Globe,
  MessageCircle,
  Utensils,
} from 'lucide-react';

export const About = () => {
  return (
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center mb-6">
          <Info className="mr-3 text-yellow-600 flex-shrink-0" size={32} />
          <h1 className="text-2xl md:text-4xl font-bold text-yellow-800">Over Frieten Bestellen</h1>
        </div>

        {/* Main Description */}
        <div className="bg-white border-2 border-yellow-600 rounded-lg shadow-sm mb-6">
          <div className="p-4 md:p-6">
            <p className="text-base md:text-lg mb-4 text-gray-700">
              Frieten Bestellen is de perfecte app voor het beheren van groepsbestellingen bij je lokale frituur.
              Geen gedoe meer met wie wat besteld heeft - alles wordt automatisch bijgehouden!
            </p>

            <p className="text-base md:text-lg mb-6 text-gray-700">
              Of je nu met vrienden, familie of collega's naar de frituur gaat, deze app maakt het eenvoudig
              om ieders bestelling te verzamelen en een overzichtelijke lijst te maken.
            </p>

            <hr className="my-6 border-yellow-200" />

            <h2 className="text-lg md:text-xl font-semibold mb-4 text-yellow-800 flex items-center gap-2">
              Waarom deze app?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start gap-3">
                <Clock className="text-yellow-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-800">Tijdsbesparing</h3>
                  <p className="text-sm text-gray-600">Geen handmatige notities meer - alles digitaal georganiseerd</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="text-yellow-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-800">Foutloos</h3>
                  <p className="text-sm text-gray-600">Automatische samenvatting voorkomt vergissingen</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Users className="text-yellow-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-800">Teamwork</h3>
                  <p className="text-sm text-gray-600">Iedereen kan meehelpen met het verzamelen van bestellingen</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Smartphone className="text-yellow-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-800">Mobiel</h3>
                  <p className="text-sm text-gray-600">Werkt perfect op je telefoon, tablet en computer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Features & How It Works Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Samen Bestellen */}
          <div className="bg-white border-2 border-yellow-600 rounded-lg shadow-sm flex flex-col justify-between">
            <div className="p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-4 text-yellow-800 flex items-center gap-2">
                <Users className="text-yellow-600 flex-shrink-0" size={20}/>
                Samen Bestellen
              </h2>
              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="text-yellow-600 flex-shrink-0" size={16}/>
                  <span className="text-sm text-yellow-800">Real-time samenwerking</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="text-yellow-600 flex-shrink-0" size={16}/>
                  <span className="text-sm text-yellow-800">Overal toegankelijk</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="text-yellow-600 flex-shrink-0" size={16}/>
                  <span className="text-sm text-yellow-800">Deel gewoon een link</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="text-yellow-600 flex-shrink-0" size={16}/>
                  <span className="text-sm text-yellow-800">Iedereen kan meebestellen</span>
                </div>
              </div>
              <p className="text-sm text-yellow-700 mb-2">
                Ideaal voor grote groepen waar iedereen zijn eigen bestelling wil invoeren.
              </p>
            </div>
          </div>
          {/* Hoe werkt het? */}
          <div className="bg-white border-2 border-yellow-600 rounded-lg shadow-sm flex flex-col justify-between">
            <div className="p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-4 text-yellow-800 flex items-center gap-2">
                <Info className="text-yellow-500 flex-shrink-0" size={20}/>
                Hoe werkt het?
              </h2>
              <div>
                <h3 className="font-semibold text-yellow-700 mb-3">Samen Bestellen:</h3>
                <ol className="space-y-2 text-sm text-yellow-800">
                  <li className="flex items-start gap-2">
                    <span className="bg-yellow-100 text-yellow-600 border border-yellow-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0">1</span>
                    <span>Maak een nieuwe room aan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-yellow-100 text-yellow-600 border border-yellow-600  rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0">2</span>
                    <span>Deel de unieke link met je groep</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-yellow-100 text-yellow-600 border border-yellow-600  rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0">3</span>
                    <span>Iedereen voegt zijn eigen bestelling toe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-yellow-100 text-yellow-600 border border-yellow-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0">4</span>
                    <span>Bekijk samen het totaaloverzicht</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};
